import * as React from 'react';
import styles from './index.less';
import { Dropdown, Input } from 'antd';
import * as AHooks from 'ahooks';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FComponentsLib from '@freelog/components-lib';
import FIcons from '@/components/FIcons';
import FSimpleBar from '../FSimpleBar';
import CascaderMenu from './CascaderMenu';
import CustomInputMode from './CustomInputMode';
import SearchPanel from './SearchPanel';
import type { Option, SelectedCache, SelectedType, SearchResult, ServerData } from './types';
import { buildOptionTree } from './types';

interface FResourceTypeInput4Props {
  disabled?: boolean;
  value: SelectedType | null;
  onChange?(value: SelectedType | null): void;
  subjectType?: 'resource' | 'collection' | 'batchResources' | 'exhibitCollection';
  onlySelect?: boolean;
  width?: number;
  showAddNewType?: boolean;
  placeholder?: string;
}

function FResourceTypeInput4({
  disabled = false,
  value,
  onChange,
  subjectType = 'resource',
  onlySelect = false,
  width = 500,
  showAddNewType = true,
  placeholder = FI18n.i18nNext.t('createresource_selectresourcetype_input_hint'),
}: FResourceTypeInput4Props) {
  const containerRef = React.useRef<HTMLDivElement>(null!);
  const overlayPanelRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<any>(null);

  const [$options, set$options] = React.useState<Option[]>([]);
  const [$recommend, set$recommend] = React.useState<{ value: string; labels: string[] }[]>([]);

  const [$selectedCache, set$selectedCache, get$selectedCache] =
    FUtil.Hook.useGetState<SelectedCache | null>(null);
  const [$autoCompleteInput, set$autoCompleteInput] = React.useState('');
  const [$autoCompleteInputIsNew, set$autoCompleteInputIsNew] = React.useState(false);
  const [$autoCompleteOptions, set$autoCompleteOptions] = React.useState<SearchResult[]>([]);
  const [$autoCompleteOptionsOther, set$autoCompleteOptionsOther] = React.useState<SearchResult[]>(
    [],
  );

  const [$searchInput, set$searchInput, get$searchInput] = FUtil.Hook.useGetState('');
  const [$searchInputState, set$searchInputState, get$searchInputState] =
    FUtil.Hook.useGetState<'focus' | 'blur'>('blur');
  const [$searchResult, set$searchResult] = React.useState<SearchResult[]>([]);
  const [$overlayMode, set$overlayMode, get$overlayMode] =
    FUtil.Hook.useGetState<'search' | 'list' | ''>('');
  const prevOverlayModeRef = React.useRef<'search' | 'list' | ''>('');

  AHooks.useEventListener('click', (e) => {
    if (containerRef.current?.contains(e.target as Node)) {
      return;
    }
    set$overlayMode('');
  }, { target: window });

  React.useLayoutEffect(() => {
    if (prevOverlayModeRef.current === 'list' && $overlayMode === 'search') {
      inputRef.current?.focus();
    }
    prevOverlayModeRef.current = $overlayMode;
  }, [$overlayMode]);

  // ===================== 数据加载 =====================

  AHooks.useMount(async () => {
    const { data }: { data: ServerData[] } = await FServiceAPI.Resource.resourceTypes({
      category: 1,
      status: 1,
      subjectType:
        subjectType === 'resource' || subjectType === 'batchResources'
          ? 1
          : subjectType === 'exhibitCollection'
            ? 5
            : subjectType === 'collection'
              ? 4
              : undefined,
      supportCreateBatch: subjectType === 'batchResources' ? 2 : undefined,
    });
    set$options(buildOptionTree(data, null));
  });

  AHooks.useMount(async () => {
    if (subjectType === 'batchResources') return;
    const {
      data,
    }: {
      data: { code: string; name: string; names: string; resourceCount: number }[];
    } = await FServiceAPI.Resource.listSimple4Recently({
      subjectType: subjectType === 'resource' ? 1 : 4,
    });
    if (!data) return;
    set$recommend(
      data.slice(0, 6).map((r) => ({ value: r.code, labels: r.names.split('/') })),
    );
  });

  // ===================== 自定义输入搜索 =====================

  AHooks.useDebounceEffect(
    () => {
      (async () => {
        if ($autoCompleteInput === '' || $autoCompleteInput.endsWith('/')) {
          set$autoCompleteOptions([]);
          set$autoCompleteOptionsOther([]);
          return;
        }
        const search = $autoCompleteInput.split('/');
        const keyword = search[search.length - 1];
        const parentCode = $selectedCache?.value || '';

        const [{ data: d1 }, { data: d2 }] = await Promise.all([
          FServiceAPI.Resource.ListSimpleByParentCode({
            parentCode,
            name: keyword,
            isTerminate: true,
          }) as Promise<{ data: { code: string; name: string; names: string; resourceCount: number }[] }>,
          FServiceAPI.Resource.ListSimpleByParentCode({
            parentCode,
            category: 1,
            name: keyword,
            excludeParentCode: true,
            isTerminate: true,
          }) as Promise<{ data: { code: string; name: string; names: string; resourceCount: number }[] }>,
        ]);

        const mapResult = (list: typeof d1): SearchResult[] =>
          list.map((l) => ({
            value: l.code,
            label: l.name,
            values: [l.code],
            labels: l.names.split('/'),
            count: l.resourceCount,
          }));

        set$autoCompleteOptions(mapResult(d1));
        set$autoCompleteOptionsOther(mapResult(d2));
      })();
    },
    [$autoCompleteInput],
    { wait: 300 },
  );

  // ===================== 搜索 =====================

  const { run: runSearch } = AHooks.useDebounceFn(
    async () => {
      if (get$searchInput() === '') {
        set$overlayMode('list');
        return;
      }

      const {
        data,
      }: {
        data: { code: string; name: string; names: string; resourceCount: number }[];
      } = await FServiceAPI.Resource.ListSimpleByParentCode({
        nameChain: get$searchInput(),
        subjectType:
          subjectType === 'resource' || subjectType === 'batchResources'
            ? 1
            : subjectType === 'collection'
              ? 4
              : subjectType === 'exhibitCollection'
                ? 5
                : undefined,
        supportCreateBatch: subjectType === 'batchResources' ? 2 : undefined,
        isTerminate: true,
        status: 1,
      });

      set$searchResult(
        data.map((l) => ({
          value: l.code,
          label: l.name,
          values: [l.code],
          labels: l.names.split('/'),
          count: l.resourceCount,
        })),
      );
      set$overlayMode('search');
    },
    { wait: 300 },
  );

  // ===================== 事件处理 =====================

  function resetCustomMode() {
    set$selectedCache(null);
    set$autoCompleteOptions([]);
    set$autoCompleteOptionsOther([]);
    set$autoCompleteInput('');
    set$autoCompleteInputIsNew(false);
  }

  function handleSelect(v: { value: string; labels: string[] }) {
    onChange?.({ value: v.value, labels: v.labels });
    set$searchInput('');
    set$overlayMode('');
    // 选中后强制切回 blur 展示，避免仍处于 focus 时选中值走 placeholder（灰色）
    set$searchInputState('blur');
    inputRef.current?.blur();
  }

  function handleClickCustom(cache: SelectedCache) {
    set$autoCompleteInput([...cache.labels, ''].join('/'));
    set$selectedCache(cache);
    set$overlayMode('');
  }

  // ===================== 自定义输入模式 =====================

  if ($selectedCache) {
    return (
      <CustomInputMode
        disabled={disabled}
        width={width}
        onlySelect={onlySelect}
        selectedCache={$selectedCache}
        autoCompleteInput={$autoCompleteInput}
        autoCompleteInputIsNew={$autoCompleteInputIsNew}
        autoCompleteOptions={$autoCompleteOptions}
        autoCompleteOptionsOther={$autoCompleteOptionsOther}
        onInputChange={(val) => {
          const startStr = [...($selectedCache.labels || []), ''].join('/');
          const custom = val.replace(startStr, '');
          set$autoCompleteInputIsNew(
            val !== startStr &&
              FUtil.Regexp.RESOURCE_TYPE.test(custom) &&
              $autoCompleteOptions.every((aco) => aco.labels.join('/') !== val),
          );
          set$autoCompleteInput(val);
        }}
        onSelect={(_value, data) => {
          handleSelect({ value: data.value, labels: data.labels });
          resetCustomMode();
        }}
        onSelectNew={() => {
          const customLabels = $autoCompleteInput.split('/');
          onChange?.({
            value: get$selectedCache()?.value || '',
            labels: get$selectedCache()?.labels || [],
            customInput: customLabels[customLabels.length - 1],
          });
          set$searchInput($autoCompleteInput);
          resetCustomMode();
        }}
        onBlur={() => set$selectedCache(null)}
      />
    );
  }

  // ===================== 正常模式（Dropdown + 级联菜单） =====================

  return (
    <Dropdown
      open={$overlayMode !== '' && !disabled}
      disabled={disabled}
      getPopupContainer={() => containerRef.current}
      destroyOnHidden={false}
      overlayClassName={styles.overlayClassName}
      placement="bottomLeft"
      autoAdjustOverflow={false}
      popupRender={() => (
        <div
          ref={overlayPanelRef}
          style={{ width, minWidth: 'auto', position: 'relative' }}
          onClick={(e) => e.stopPropagation()}
        >
          {$searchInput === '' && $overlayMode === 'list' && (
            <FSimpleBar maxHeight={540}>
              <div
                className={styles.overlay}
                style={{ width }}
                onClick={(e) => e.stopPropagation()}
              >
                {$recommend.length > 0 && (
                  <>
                    {subjectType === 'resource' && (
                      <div className={styles.recommend}>
                        <FComponentsLib.FContentText
                          text={FI18n.i18nNext.t('createresource_selectresourcetype_input_hint2')}
                          type="additional2"
                        />
                        <div style={{ height: 20 }} />
                        <div className={styles.recommendResourceTypes}>
                          {$recommend.map((r) => (
                            <label
                              key={r.value}
                              onClick={() => handleSelect({ value: r.value, labels: r.labels })}
                            >
                              {r.labels.join('/')}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {subjectType === 'resource' && (
                      <div style={{ padding: '0 20px' }}>
                        <FComponentsLib.FContentText
                          text={FI18n.i18nNext.t('createresource_selectresourcetype_input_hint3')}
                          type="additional2"
                        />
                      </div>
                    )}
                    <div style={{ height: 10 }} />
                  </>
                )}

                <div className={styles.FCascader}>
                  <CascaderMenu
                    options={$options}
                    showAddNewType={showAddNewType}
                    // 挂到下拉面板内，避免级联 Popover 落在触发器上导致选中后 click 冒泡重新打开
                    getPopupContainer={() =>
                      overlayPanelRef.current || containerRef.current
                    }
                    onSelect={handleSelect}
                    onClickCustom={handleClickCustom}
                    onFocusInput={() => inputRef.current?.focus()}
                  />
                </div>
                <div style={{ height: 10 }} />
              </div>
            </FSimpleBar>
          )}

          {$overlayMode === 'search' && (
            <FSimpleBar maxHeight={540}>
              <div className={styles.overlay} onClick={(e) => e.stopPropagation()}>
                <SearchPanel
                  results={$searchResult}
                  subjectType={subjectType}
                  showAddNewType={showAddNewType}
                  onSelect={handleSelect}
                  onBack={() => {
                    set$searchInput('');
                    runSearch();
                  }}
                />
              </div>
            </FSimpleBar>
          )}
        </div>
      )}
    >
      <div
        className={styles.square}
        style={{
          ...(disabled ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}),
          width,
        }}
        ref={containerRef}
        onClick={(e) => {
          e.stopPropagation();
          set$overlayMode(get$searchInput() === '' ? 'list' : 'search');
          runSearch();
        }}
      >
        <Input
          disabled={disabled}
          ref={inputRef}
          style={{ width, height: 38 }}
          placeholder={
            $searchInputState === 'focus' && !!value
              ? [...value.labels, value.customInput].filter(Boolean).join('/')
              : placeholder
          }
          value={
            $searchInputState === 'blur' && !!value
              ? [...value.labels, value.customInput].filter(Boolean).join('/')
              : $searchInput
          }
          onChange={(e) => {
            const next = e.target.value;
            set$searchInput(next);
            set$overlayMode(next === '' ? 'list' : 'search');
            runSearch();
          }}
          onFocus={() => {
            if (get$searchInputState() === 'blur') {
              set$searchInput('');
            }
            set$searchInputState('focus');
          }}
          onBlur={() => {
            window.setTimeout(() => {
              if (containerRef.current?.contains(document.activeElement)) {
                return;
              }
              if (get$searchInput() !== '') {
                return;
              }
              set$searchInput('');
              set$searchInputState('blur');
            }, 100);
          }}
          suffix={
            <div
              style={{ visibility: $searchInput === '' ? 'visible' : 'hidden', width: 12, flexShrink: 0 }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                set$overlayMode($overlayMode !== '' ? '' : 'list');
              }}
            >
              {$overlayMode !== '' && !disabled ? (
                <FIcons.FUp style={{ fontSize: 12 }} />
              ) : (
                <FIcons.FDown style={{ fontSize: 12 }} />
              )}
            </div>
          }
        />
      </div>
    </Dropdown>
  );
}

export default FResourceTypeInput4;
